import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import {
  U21Button,
  U21MenuList,
  U21MenuListOptionProps,
  U21Spacer,
  U21Tooltip,
} from 'app/shared/u21-ui/components';
import {
  IconAlertTriangle,
  IconCheck,
  IconPencil,
  IconTrash,
  IconUsers,
  IconUserShare,
} from '@u21/tabler-icons';

import { DeleteSavedFilterModal } from 'app/modules/filters/components/DeleteSavedFilterModal';
import { EditSavedFilterModal } from 'app/modules/filters/components/EditSavedFilterModal';
import { Filter, FilterContext, SavedFilter } from 'app/modules/filters/models';
import { selectAgent } from 'app/modules/session/selectors';
import { useTheme } from '@mui/material/styles';
import pluralize from 'pluralize';

interface Props {
  appliedSavedFilterIds: Set<number>;
  currentFilters: Filter[];
  filterContext: FilterContext;
  onApply: (savedFilter: SavedFilter) => void;
  onDeleted: (savedFilter: SavedFilter) => void;
  onUpdated?: (savedFilter: SavedFilter) => void;
  savedFilters: SavedFilter[];
  staleSavedFilterIds: Set<number>;
}

export const SavedFilterPanel = ({
  appliedSavedFilterIds,
  currentFilters,
  filterContext,
  onApply,
  onDeleted,
  onUpdated,
  savedFilters,
  staleSavedFilterIds,
}: Props) => {
  const agent = useSelector(selectAgent);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SavedFilter | undefined>(
    undefined,
  );
  const [editOpen, setEditOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<SavedFilter | undefined>(
    undefined,
  );

  const theme = useTheme();

  const options = useMemo<U21MenuListOptionProps[]>(() => {
    return savedFilters.map((i) => {
      const isOwner = i.created_by_id === agent.id;
      const isApplied = appliedSavedFilterIds.has(i.id);
      const isStale = staleSavedFilterIds.has(i.id);

      const sharedTeamCount = i.shared_team_ids.length;

      const rightIcon = (
        <IconContainer>
          <U21Spacer horizontal>
            {!isOwner && (
              <U21Tooltip tooltip={`Shared by ${i.created_by_name}`}>
                <IconUsers color={theme.palette.text.secondary} />
              </U21Tooltip>
            )}
            {isOwner && sharedTeamCount > 0 && (
              <U21Tooltip
                tooltip={`Shared with ${pluralize('team', sharedTeamCount, true)}`}
              >
                <IconUserShare color={theme.palette.text.secondary} />
              </U21Tooltip>
            )}
            {isApplied && <IconCheck color={theme.palette.primary.main} />}
            {!isApplied && isStale && (
              <U21Tooltip tooltip="Some filters in this saved filter may be unavailable.">
                <IconAlertTriangle color={theme.palette.warning.main} />
              </U21Tooltip>
            )}
          </U21Spacer>
          {isOwner && (
            <>
              <ActionButton
                aria-label={`Edit ${i.title}`}
                icon={<IconPencil />}
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingEdit(i);
                  setEditOpen(true);
                }}
                size="small"
                variant="ghost"
              />
              <ActionButton
                aria-label={`Delete ${i.title}`}
                color="error"
                icon={<IconTrash />}
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete(i);
                  setDeleteOpen(true);
                }}
                size="small"
                variant="ghost"
              />
            </>
          )}
        </IconContainer>
      );

      return {
        description: i.description ?? undefined,
        onClick: () => onApply(i),
        rightIcon,
        text: i.title,
        typographyProps: { ellipsis: true },
      };
    });
  }, [
    agent.id,
    appliedSavedFilterIds,
    staleSavedFilterIds,
    savedFilters,
    onApply,
    theme,
  ]);

  return (
    <>
      <U21MenuList options={options} placeholder="Search saved filters" />
      {pendingDelete && (
        <DeleteSavedFilterModal
          filterContext={filterContext}
          onClose={() => setDeleteOpen(false)}
          onDeleted={onDeleted}
          open={deleteOpen}
          savedFilter={pendingDelete}
        />
      )}
      {pendingEdit && (
        <EditSavedFilterModal
          filters={currentFilters}
          onClose={() => setEditOpen(false)}
          onUpdated={onUpdated}
          open={editOpen}
          savedFilter={pendingEdit}
        />
      )}
    </>
  );
};

const IconContainer = styled.div`
  display: flex;
`;

const ActionButton = styled(U21Button)`
  // cancel out the delete button padding so the icons are aligned across rows
  margin-right: -5px;
  max-width: 0;
  overflow: hidden;
  padding-left: 0;
  padding-right: 0;
  transition: ${(props) => props.theme.transitions.create(['all'])};

  // reveal on row hover or when the button itself receives keyboard focus
  li:hover &,
  &:focus-visible {
    margin-right: -5px; // -5px cancels out button padding
    margin-left: 3px; // add 3px margin so w/ button padding, it makes 8px
    max-width: 30px;
    padding-left: 5px;
    padding-right: 5px;
  }
`;
